import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  keyframes,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiChevronRight,
  FiGitBranch,
} from 'react-icons/fi';
import moment from 'moment';
import {
  ICON_MAP,
  COLOR_MAP_HEX,
  COLOR_MAP,
  STATUS_COLOR,
  getSummary,
  getNodeLabel,
} from './campaignConstants';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(49, 130, 206, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(49, 130, 206, 0); }
  100% { box-shadow: 0 0 0 0 rgba(49, 130, 206, 0); }
`;

function NodeStepCard({ node, status, queueItem, isLast, isBranch }) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const dimmedBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const connectorColor = useColorModeValue('gray.300', 'gray.600');
  const dimmedText = useColorModeValue('gray.400', 'gray.500');

  const NodeIcon = ICON_MAP[node.subType] || FiChevronRight;
  const nodeColor = COLOR_MAP_HEX[node.subType] || '#A0AEC0';
  const label = getNodeLabel(node.subType);
  const summary = getSummary(node);

  const isCompleted = status === 'completed';
  const isCurrent = status === 'pending' || status === 'processing';
  const isFailed = status === 'failed';
  const isUpcoming = !isCompleted && !isCurrent && !isFailed;

  let leftBorderColor = borderColor;
  let StatusIcon = null;
  let statusIconColor = '';

  if (isCompleted) {
    leftBorderColor = '#38A169';
    StatusIcon = FiCheckCircle;
    statusIconColor = 'green.500';
  } else if (isCurrent) {
    leftBorderColor = '#3182CE';
    StatusIcon = FiClock;
    statusIconColor = 'blue.500';
  } else if (isFailed) {
    leftBorderColor = '#E53E3E';
    StatusIcon = FiAlertCircle;
    statusIconColor = 'red.500';
  }

  return (
    <Box position="relative">
      <HStack spacing={0} align="stretch">
        {/* Connector line */}
        <Flex direction="column" align="center" w="32px" flexShrink={0}>
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg={isUpcoming ? dimmedBg : 'white'}
            border="2px solid"
            borderColor={leftBorderColor}
            align="center"
            justify="center"
            zIndex={1}
            animation={isCurrent ? `${pulse} 2s infinite` : undefined}
            _dark={{ bg: isUpcoming ? 'gray.800' : 'gray.700' }}
          >
            {StatusIcon ? (
              <Icon as={StatusIcon} color={statusIconColor} boxSize="14px" />
            ) : (
              <Icon as={NodeIcon} color={dimmedText} boxSize="12px" />
            )}
          </Flex>
          {!isLast && (
            <Box
              w="2px"
              flex="1"
              bg={isCompleted ? 'green.300' : connectorColor}
              minH="8px"
            />
          )}
        </Flex>

        {/* Step card */}
        <Box
          flex="1"
          ml={3}
          mb={isLast ? 0 : 3}
          p={3}
          bg={isUpcoming ? dimmedBg : cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderLeft="3px solid"
          borderLeftColor={leftBorderColor}
          borderRadius="md"
          opacity={isUpcoming ? 0.65 : 1}
          transition="all 0.15s"
        >
          <HStack justify="space-between" mb={1}>
            <HStack spacing={2}>
              <Flex
                w="24px"
                h="24px"
                borderRadius="md"
                bg={`${nodeColor}18`}
                align="center"
                justify="center"
              >
                <Icon as={NodeIcon} color={nodeColor} boxSize="13px" />
              </Flex>
              <Text fontSize="sm" fontWeight="600" color={isUpcoming ? dimmedText : undefined}>
                {label}
              </Text>
              {isBranch && (
                <Icon as={FiGitBranch} color="purple.400" boxSize="12px" />
              )}
            </HStack>
            <HStack spacing={2}>
              {queueItem && (
                <Badge
                  colorScheme={STATUS_COLOR[queueItem.status] || 'gray'}
                  fontSize="2xs"
                  variant="subtle"
                >
                  {queueItem.status}
                </Badge>
              )}
            </HStack>
          </HStack>

          <Text fontSize="xs" color={isUpcoming ? dimmedText : 'gray.500'} noOfLines={1}>
            {summary}
          </Text>

          {/* Execution details for completed/failed nodes */}
          {isCompleted && queueItem?.executionResult?.summary && (
            <Text fontSize="xs" color="green.600" mt={1} _dark={{ color: 'green.300' }}>
              {queueItem.executionResult.summary}
            </Text>
          )}
          {isFailed && queueItem?.errorLog && (
            <Text fontSize="xs" color="red.500" mt={1} noOfLines={2}>
              {queueItem.errorLog}
            </Text>
          )}

          {/* Timestamp */}
          {queueItem?.completedAt && isCompleted && (
            <Text fontSize="2xs" color="gray.400" mt={1}>
              {moment(queueItem.completedAt).format('MMM D, h:mm A')}
            </Text>
          )}
          {isCurrent && queueItem?.executeAfter && (
            <Text fontSize="2xs" color="blue.400" mt={1}>
              Scheduled: {moment(queueItem.executeAfter).format('MMM D, h:mm A')}
              {moment(queueItem.executeAfter).isAfter(moment()) &&
                ` (${moment(queueItem.executeAfter).fromNow()})`}
            </Text>
          )}
        </Box>
      </HStack>
    </Box>
  );
}

function WhatsNextCard({ nextNode }) {
  const bg = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('blue.200', 'blue.700');
  const NodeIcon = ICON_MAP[nextNode.subType] || FiChevronRight;

  return (
    <Box
      mt={3}
      p={3}
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
    >
      <HStack spacing={2}>
        <Icon as={FiChevronRight} color="blue.500" />
        <Text fontSize="sm" fontWeight="600" color="blue.600" _dark={{ color: 'blue.300' }}>
          What&apos;s Next
        </Text>
      </HStack>
      <HStack spacing={2} mt={1}>
        <Icon as={NodeIcon} color={COLOR_MAP_HEX[nextNode.subType] || '#A0AEC0'} boxSize="14px" />
        <Text fontSize="sm">
          {getNodeLabel(nextNode.subType)}: {getSummary(nextNode)}
        </Text>
      </HStack>
    </Box>
  );
}

export default function CampaignProgressFlow({
  nodes,
  actionNodes,
  completedNodeIds,
  failedNodeIds,
  currentStep,
  nodeQueueMap,
}) {
  const filteredNodes = actionNodes && actionNodes.length > 0 ? actionNodes : nodes;

  // Find the "what's next" node — the node after the current step
  let nextNode = null;
  if (currentStep?.node?.nextNodeId) {
    nextNode = nodes.find((n) => n.id === currentStep.node.nextNodeId);
  }

  return (
    <VStack align="stretch" spacing={0} py={2}>
      {filteredNodes.map((node, idx) => {
        const queueItem = nodeQueueMap?.get(node.id);
        let status = 'upcoming';
        if (completedNodeIds.has(node.id)) status = 'completed';
        else if (failedNodeIds.has(node.id)) status = 'failed';
        else if (currentStep?.node?.id === node.id) status = currentStep.queueItem?.status || 'pending';

        const isBranch = node.subType === 'IF_ELSE' || node.subType === 'SWITCH' || node.subType === 'AB_SPLIT';

        return (
          <NodeStepCard
            key={node.id}
            node={node}
            status={status}
            queueItem={queueItem}
            isLast={idx === filteredNodes.length - 1}
            isBranch={isBranch}
          />
        );
      })}

      {nextNode && currentStep && <WhatsNextCard nextNode={nextNode} />}
    </VStack>
  );
}
